import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod/v4';
import { api } from '@shared/api';

const locationIdSchema = z
  .string()
  .regex(/^gid:\/\/shopify\/Location\/[0-9]+$/u)
  .max(100);

const proposalSchema = z.object({
  scope: z.object({
    storeId: z.string(),
    locationId: z.string(),
    warehouseId: z.string(),
    mappingRevision: z.string(),
  }),
  variantId: z.string(),
  inventoryItemId: z.string(),
  originalSku: z.string().nullable(),
  sourceProductId: z.string().nullable(),
  sourceSku: z.string().nullable(),
  match: z.enum(['exact_sku', 'override']).nullable(),
  evidenceReference: z.string().nullable(),
  writesAllowed: z.literal(false),
  status: z.enum(['blocked', 'changed', 'aligned']),
  reason: z
    .enum([
      'invalid_scope',
      'invalid_identity',
      'duplicate_identity',
      'invalid_override',
      'missing_product',
      'ambiguous_product',
      'unapproved_shared_stock',
      'untracked',
      'missing_balance',
      'ambiguous_balance',
      'ineligible_source',
      'invalid_quantity',
      'missing_observation',
      'ambiguous_observation',
    ])
    .nullable(),
  desiredQuantity: z.number().int().nonnegative().nullable(),
  observedQuantity: z.number().int().nullable(),
  difference: z.number().int().nullable(),
});

export const stockProposalsSchema = z.object({
  data: z.object({ items: z.array(proposalSchema).max(100) }),
  meta: z.object({
    page: z.number().int().min(1).max(100_000),
    pageSize: z.number().int().min(1).max(100),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    summary: z.object({
      changed: z.number().int().nonnegative(),
      blocked: z.number().int().nonnegative(),
      aligned: z.number().int().nonnegative(),
    }),
    snapshotGeneratedAt: z.iso.datetime(),
    snapshotExpiresAt: z.iso.datetime(),
    storeDomain: z.string(),
    locationId: locationIdSchema,
    warehouseSourceRecordId: z.string(),
    mappingRevision: z.number().int().nonnegative(),
    synchronizationReference: z.string(),
    lastSuccessfulStockSyncAt: z.iso.datetime().nullable(),
    writesAllowed: z.literal(false),
    ruleApprovalReference: z.string().min(1),
  }),
});

export type StockProposalReview = z.infer<typeof stockProposalsSchema>;
export type StockProposalRow = StockProposalReview['data']['items'][number];

export const stockProposalQuerySchema = z.strictObject({
  page: z.number().int().min(1).max(100_000).default(1),
  pageSize: z.number().int().min(1).max(100).default(25),
  q: z.string().trim().max(200).default(''),
  status: z.enum(['all', 'blocked', 'changed', 'aligned']).default('all'),
  refresh: z.boolean().default(false),
});
export type StockProposalQuery = z.input<typeof stockProposalQuerySchema>;

/** Reads one location's preview. This facade has no mutation method. */
export async function getStockProposals(
  locationId: string,
  signal: AbortSignal,
  query: StockProposalQuery = {}
): Promise<StockProposalReview> {
  const response = await api.get('/api/v1/shopify/stock-proposals', {
    params: {
      locationId: locationIdSchema.parse(locationId),
      ...stockProposalQuerySchema.parse(query),
    },
    signal,
    timeout: 30_000,
  });
  return stockProposalsSchema.parse(response);
}

export function stockProposalsQuery(
  viewerAccessScope: string,
  locationId: string,
  query: StockProposalQuery = {}
) {
  const normalized = stockProposalQuerySchema.parse(query);
  return queryOptions({
    queryKey: ['shopify-stock-proposals', viewerAccessScope, locationId, normalized] as const,
    queryFn: ({ signal }) => getStockProposals(locationId, signal, normalized),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
